import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { Drawer, List, ListItem, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util';
import { maybe } from '@lwtears/lwt-common-frontend/lib/@common/util/maybe-util';
import useTelemetry from '../../hooks/use-telemetry';
import ButtonWithFontAwesome from '../common/Buttons/ButtonWithFontAwesome';
import SSOLogoutFrame from '../common/SSOLogoutFrame';
import { getNavigationItems } from '../router/NavigationItems';
import { RouteSource } from '../../graph/schema-generated-types-educator';
import { TelemetryEvents } from '../../types';
import { remCalc } from '../../util/style-util';
import { disabled } from '../../styles/sharedStyles';

const t = (key) => translate('menu', key);

type NavigationMenuProps = {
  open: boolean;
  toggle: () => void;
  omitRoutes?: string[];
};

const NavigationMenu: React.FC<NavigationMenuProps> = ({ open, toggle, omitRoutes }) => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_LINK);
  const classes = useStyles();
  const filteredNavigationItems = useMemo(() => {
    return getNavigationItems().filter((x) => !omitRoutes?.includes(x.to));
  }, [omitRoutes]);

  const handleClick = (title?: string, sendEvent?: boolean, link?: string, newWindow?: boolean) => {
    if (link && newWindow) window.open(link, '_blank');
    if (!sendEvent) return;
    fireEvent({ link: title, route: RouteSource.MAIN_MENU });
  };

  const [hasLoggedOut, setHasLoggedOut] = useState(false);

  const handleLogout = (event: MouseEvent) => {
    setHasLoggedOut(true);
    event.stopPropagation();
  };

  // const MaybeLogout: any = maybe(Logout);
  const MaybeSSOLogout: any = maybe(SSOLogoutFrame);

  return (
    <Drawer
      PaperProps={{ className: classes.menuPaperClass }}
      open={open}
      onClose={toggle}
      anchor="left"
    >
      {filteredNavigationItems.map(
        ({ text, to, from, sendTelemetryEvent, title, disabled, newWindow }, idx) => (
          <List
            className={classes.list}
            key={`navigation_list_item_${idx}`}
            onClick={() => handleClick(title, sendTelemetryEvent, to, newWindow)}
          >
            {disabled ? (
              <ListItem className={classes.listItem}>
                <Typography className={classes.disabled}>{text}</Typography>
              </ListItem>
            ) : (
              <Link to={{ pathname: to }} state={{ from }}>
                <ListItem className={classes.listItem}>
                  <Typography>{text}</Typography>
                </ListItem>
              </Link>
            )}
            <hr className={classes.line} />
          </List>
        )
      )}
      <ButtonWithFontAwesome
        onClick={handleLogout}
        startIcon={faSignOutAlt}
        text={t('logout')}
        aria-label={t('logout')}
        variant="text"
        className={classes.logoutButton}
      />
      <MaybeSSOLogout shouldRender={hasLoggedOut} />
      <div
        className={classes.listItem}
        style={{ textAlign: 'center', position: 'fixed', bottom: 0 }}
      >
        <p>Version: {process.env.REACT_APP_APP_VERSION}</p>
      </div>
    </Drawer>
  );
};

const useStyles = makeStyles((theme) => ({
  menuPaperClass: {
    backgroundColor: `${theme.palette.grey[900]} !important`,
    width: 355,
    maxWidth: '18rem',
    display: 'flex',
    height: '100%',
    top: remCalc(theme.constants.app.menuOffset),
  },
  list: {
    padding: '0px',
    '&:first-child': {
      paddingTop: '10px',
    },
    '& a': {
      textDecoration: 'none !important',
    },
  },
  logoutButton: {
    ...theme.typography.caption,
    padding: '10px 15px 5px 25px',
    color: 'white',
    justifyContent: 'left',
  },
  listItem: {
    padding: '10px 15px 5px 25px',

    '& p': {
      ...theme.typography.caption,
      color: 'white !important',
      padding: '0px',
    },
  },
  line: {
    padding: '0px',
    border: 'none',
    height: '1px',
    color: theme.palette.grey[600],
    backgroundColor: theme.palette.grey[600],
  },
  disabled: disabled,
}));

export default NavigationMenu;
